import React, { useEffect, useState } from 'react';
import API from '../utils/api';
import { toast } from 'react-toastify';
import ToggleSwitch from '../components/ToggleSwitch';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';
const PAGE_SIZE = 12;
const EMPTY_FORM = { title: '', description: '', type: 'image', category: 'cows', placement: 'gallery', url: '', order: 0 };

export default function ManageGallery() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [file, setFile] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [viewItem, setViewItem] = useState(null);

  const fetchItems = (p = page) => {
    setLoading(true);
    API.get(`/gallery/admin/list?page=${p}&limit=${PAGE_SIZE}`)
      .then(r => { setItems(r.data.items); setTotalPages(r.data.totalPages); setPage(r.data.page); setLoading(false); })
      .catch(() => setLoading(false));
  };
  useEffect(() => { fetchItems(1); }, []);

  const openAddForm = () => { setEditingId(null); setForm(EMPTY_FORM); setFile(null); setShowForm(true); };
  const openEditForm = (item) => {
    setEditingId(item._id);
    setForm({ title: item.title, description: item.description || '', type: item.type, category: item.category, placement: item.placement, url: item.url?.startsWith('http') ? item.url : '', order: item.order || 0 });
    setFile(null);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.title) return toast.error('Title required');
    if (!editingId && !file && !form.url) return toast.error('Upload a file or provide a URL');
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        // Don't send an empty url — it would overwrite an existing uploaded
        // file's path with '' when editing without picking a new file/URL.
        if (k === 'url' && !v) return;
        fd.append(k, v);
      });
      if (file) fd.append('file', file);
      if (editingId) {
        await API.put(`/gallery/${editingId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Gallery item updated');
      } else {
        await API.post('/gallery', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Gallery item added as draft — use the switch to publish it');
      }
      setShowForm(false); setFile(null); setEditingId(null); setForm(EMPTY_FORM);
      fetchItems(editingId ? page : 1);
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove item?')) return;
    await API.delete(`/gallery/${id}`);
    toast.success('Item removed');
    fetchItems(items.length === 1 && page > 1 ? page - 1 : page);
  };

  const togglePublish = async (item, nextValue) => {
    await API.put(`/gallery/${item._id}`, { isPublished: nextValue });
    toast.success(nextValue ? 'Item published — now visible on the site' : 'Item unpublished');
    fetchItems(page);
  };

  const getUrl = item => item.url?.startsWith('http') ? item.url : `${API_BASE}${item.url}`;

  const placementLabel = p => ({ gallery: 'Gallery Only', banner: 'Banner Only', both: 'Banner + Gallery' }[p] || p);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 className="admin-page-title" style={{ margin: 0 }}>📸 Manage Gallery</h2>
        <button className="btn btn-primary" onClick={() => (showForm ? setShowForm(false) : openAddForm())}>+ Add Item</button>
      </div>

      {showForm && (
        <div className="admin-form-card" style={{ marginBottom: 24 }}>
          <h3 style={{ fontWeight: 700, marginBottom: 16 }}>{editingId ? 'Edit Gallery Item' : 'Add Gallery Item'}</h3>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Title *</label><input className="form-input" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} /></div>
            <div className="form-group">
              <label className="form-label">Type</label>
              <select className="form-select" value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))}>
                <option value="image">🖼️ Image</option><option value="video">🎥 Video</option>
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Category</label>
              <select className="form-select" value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>
                <option value="cows">🐂 Cows</option><option value="activities">🌾 Activities</option><option value="festivals">🎉 Festivals</option><option value="infrastructure">🏗️ Infrastructure</option><option value="events">📅 Events</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Show In</label>
              <select className="form-select" value={form.placement} onChange={e => setForm(p => ({ ...p, placement: e.target.value }))}>
                <option value="gallery">🖼️ Gallery Only</option>
                <option value="banner">🎞️ Home Banner Only</option>
                <option value="both">🔁 Banner + Gallery</option>
              </select>
            </div>
          </div>
          <div className="form-group"><label className="form-label">Display Order</label><input className="form-input" type="number" value={form.order} onChange={e => setForm(p => ({ ...p, order: e.target.value }))} /></div>
          <div className="form-group"><label className="form-label">{editingId ? 'Replace File (optional)' : 'Upload File (or enter URL below)'}</label><input type="file" accept="image/*,video/*" onChange={e => setFile(e.target.files[0])} /></div>
          <div className="form-group"><label className="form-label">Or External URL</label><input className="form-input" value={form.url} onChange={e => setForm(p => ({ ...p, url: e.target.value }))} placeholder="https://..." /></div>
          <div className="form-group"><label className="form-label">Description</label><textarea className="form-input" rows={2} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} /></div>
          {!editingId && <p style={{ fontSize: '0.82rem', color: '#888', marginBottom: 14 }}>New items are saved as a draft. They won't appear on the site until you flip the switch to <strong>Published</strong>.</p>}
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-primary" onClick={handleSave}>{editingId ? 'Save Changes' : 'Add to Gallery'}</button>
            <button className="btn btn-outline" onClick={() => { setShowForm(false); setEditingId(null); }}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div className="loader"><div className="spinner" /></div> : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
            {items.map(item => (
              <div key={item._id} style={{ background: '#fff', borderRadius: 10, overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', position: 'relative' }}>
                {item.type === 'video'
                  ? <div onClick={() => setViewItem(item)} style={{ height: 140, background: '#222', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '3rem', cursor: 'pointer' }}>🎥</div>
                  : <img src={getUrl(item)} alt={item.title} onClick={() => setViewItem(item)} style={{ width: '100%', height: 140, objectFit: 'cover', display: 'block', cursor: 'pointer' }} />
                }
                <div style={{ padding: '10px 12px' }}>
                  <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: 4 }}>{item.title}</div>
                  <div style={{ fontSize: '0.78rem', color: '#888', marginBottom: 10 }}>{item.category} • {item.type} • {placementLabel(item.placement)}</div>
                  <ToggleSwitch checked={item.isPublished} onChange={(v) => togglePublish(item, v)} />
                  <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                    <button className="btn btn-sm btn-outline" style={{ flex: 1, justifyContent: 'center' }} onClick={() => setViewItem(item)}>👁️ View</button>
                    <button className="btn btn-sm btn-outline" style={{ flex: 1, justifyContent: 'center' }} onClick={() => openEditForm(item)}>✏️ Edit</button>
                  </div>
                </div>
                <button onClick={() => handleDelete(item._id)} style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(198,40,40,0.9)', color: '#fff', border: 'none', borderRadius: '50%', width: 28, height: 28, cursor: 'pointer', fontSize: '0.8rem' }}>✕</button>
              </div>
            ))}
            {items.length === 0 && (
              <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: 40, color: '#888' }}>No gallery items yet. Add photos and videos!</div>
            )}
          </div>

          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 28 }}>
              <button className="btn btn-sm btn-outline" disabled={page <= 1} onClick={() => fetchItems(page - 1)}>← Prev</button>
              <span style={{ fontSize: '0.9rem', color: '#555' }}>Page {page} of {totalPages}</span>
              <button className="btn btn-sm btn-outline" disabled={page >= totalPages} onClick={() => fetchItems(page + 1)}>Next →</button>
            </div>
          )}
        </>
      )}

      {viewItem && (
        <div onClick={() => setViewItem(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, cursor: 'pointer' }}>
          {viewItem.type === 'video'
            ? <video src={getUrl(viewItem)} controls autoPlay style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: 10 }} onClick={e => e.stopPropagation()} />
            : <img src={getUrl(viewItem)} alt={viewItem.title} style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: 10, objectFit: 'contain' }} onClick={e => e.stopPropagation()} />
          }
        </div>
      )}
    </div>
  );
}
